import template from "./template.html"
import controlsTemplate from "./controls.html"
import timerTemplate from "./timer.html"
import "./style.css"
import UIComponent from "../../common/types/ui-component";
import { htmlToElement } from "../../common/services/html-to-element";
import { CatalogAccess } from "../../common/services/catalog/catalog-access";
import SavefileAccess from "../../common/services/savefile/savefile-access";
import { NonogramBoardComponent } from "../../common/components/nonogram-board/nonogram-board.component";
import BoxComponent from "../../common/components/box/box.component";
import Color from "../../common/types/color";
import NonogramKeyboardListener from "./key-listener";
import { NonogramColor, NonogramComponentState } from "./state";
import NonogramViewUpdater from "./listeners/view-update";
import NonogramButtonsListener from "./listeners/buttons-listener";
import NonogramMouseControlsHandler from "./mouse-controls";
import SolverButtonHandler from "./solver-button-handler";
import SolvedListener from "./listeners/solved-listener";
import SaveListener from "./listeners/save-listener";
import { SavefileUtils } from "../../common/services/savefile/savefile-utils";
import { CellKnowledge, NonogramState } from "../../common/types/nonogram-types";
import { PLAYFIELD_TITLE } from "../../common/titles";
import AuthService from "../../common/services/auth/auth-service";
import KeyboardControlsExplainer from "./keyboard-controls-explainer/keyboard-controls-explainer";
import DesktopRoot from "../root-component/desktop-root";

export default class NonogramPage implements UIComponent
{

    public readonly view: HTMLElement;

    constructor(
        private readonly nonogramId: string,
        private readonly catalogAccess: CatalogAccess,
        private readonly savefileAccess: SavefileAccess,
        private readonly authService: AuthService,
        private readonly root: DesktopRoot
    )
    {
        this.view = htmlToElement(template);
    }

    async create(parent: HTMLElement): Promise<HTMLElement> {
        const nonogramContainer = this.view.querySelector("#nonogram-container") as HTMLDivElement;
        const leftCol = this.view.querySelector(".left") as HTMLDivElement;
        const middleCol = this.view.querySelector(".center") as HTMLDivElement;
        const rightCol = this.view.querySelector(".right") as HTMLDivElement;

        /* Create controls */
        const controlsDiv = htmlToElement(controlsTemplate);
        const controlsBox = new BoxComponent("Controls", Color.YELLOW);
        controlsBox.create(leftCol);
        controlsBox.view.classList.add("controls-box");
        controlsBox.content.appendChild(controlsDiv);

        /* Create link for keyboard control explanation */
        const explainer = new KeyboardControlsExplainer(this.root);
        const controlsExplanationLink = document.createElement("a");
        controlsExplanationLink.href = "#";
        controlsExplanationLink.onclick = () => explainer.run();
        controlsExplanationLink.textContent = "Explain keyboard controls...";
        controlsExplanationLink.style.fontSize = "10pt";
        leftCol.appendChild(controlsExplanationLink);


        /* Create nonogram board */
        const nonogramBox = new BoxComponent("Nonogram View", Color.RED);
        nonogramBox.create(middleCol);

        const nonogram = await this.catalogAccess.getNonogram(this.nonogramId);
        if (!nonogram) {
            throw new Error("Nonogram with id " + this.nonogramId + "doesn't exist.");
        }

        document.title = PLAYFIELD_TITLE(nonogram.colHints.length, nonogram.rowHints.length);

        const savefile = await this.savefileAccess.getSavefile();
        const savestate = SavefileUtils.getSavestateForNonogram(savefile, this.nonogramId);
        const history = savestate == undefined ? undefined :
            SavefileUtils.calculateAllStates(nonogram.colHints.length, nonogram.rowHints.length, savestate.history)
                .map(x => new NonogramState(nonogram.rowHints, nonogram.colHints, x));
        
        const state = new NonogramComponentState(
            this.nonogramId,
            nonogram.rowHints,
            nonogram.colHints,
            history,
            savestate?.elapsed || 0
        );
        const board = new NonogramBoardComponent(nonogram.rowHints, nonogram.colHints);
        board.create(nonogramBox.content);
        parent.appendChild(this.view);

        new NonogramMouseControlsHandler(state, board);

        /* Create solver message panel */
        const solverMsg = document.createElement("span");
        solverMsg.id = "solver-msg";
        solverMsg.style.maxWidth = nonogramBox.view.getBoundingClientRect().width + "px";
        middleCol.appendChild(solverMsg);

        /* Create timer */
        const timerBox = new BoxComponent("Timer", Color.GREEN);
        timerBox.create(rightCol);

        const timerDiv = htmlToElement(timerTemplate);
        timerBox.content.appendChild(timerDiv);

        /* Buttons */
        const btnBlack = this.view.querySelector("#btn-black") as HTMLButtonElement;
        btnBlack.onclick = () => state.chosenColor = NonogramColor.BLACK;

        const btnWhite = this.view.querySelector("#btn-white") as HTMLButtonElement;
        btnWhite.onclick = () => state.chosenColor = NonogramColor.WHITE;

        const btnUndo = this.view.querySelector("#btn-undo") as HTMLButtonElement;
        btnUndo.onclick = () => state.undo();

        const btnRedo = this.view.querySelector("#btn-redo") as HTMLButtonElement;
        btnRedo.onclick = () => state.redo();

        /* Solver buttons */
        const solverButtonHandler = new SolverButtonHandler(state);

        const btnHint = this.view.querySelector("#btn-hint") as HTMLButtonElement;
        btnHint.onclick = () => solverButtonHandler.hint();

        const btnNext = this.view.querySelector("#btn-next") as HTMLButtonElement;
        btnNext.onclick = () => solverButtonHandler.solveNext();

        const btnSolve = this.view.querySelector("#btn-solve") as HTMLButtonElement;
        btnSolve.onclick = () => solverButtonHandler.solve();

        /* Reset buttons */
        const btnResetToValid = this.view.querySelector("#btn-reset-to-valid") as HTMLButtonElement;
        btnResetToValid.onclick = () => state.historyIdx = state.lastValidHistoryIdx;

        const btnReset = this.view.querySelector("#btn-reset") as HTMLButtonElement;
        btnReset.onclick = () => state.reset();

        /* State Listeners */
        const timerSpan = this.view.querySelector(".timer .elapsed") as HTMLSpanElement;
        const viewUpdater = new NonogramViewUpdater(state, board, solverMsg, timerSpan);
        state.listeners.push(viewUpdater);

        const buttonsUpdater = new NonogramButtonsListener(state, btnUndo, btnRedo, btnBlack, btnWhite, btnResetToValid);
        state.listeners.push(buttonsUpdater);

        const solvedListener = new SolvedListener(state);
        state.listeners.push(solvedListener);

        const saveListener = new SaveListener(state, this.savefileAccess);
        state.listeners.push(saveListener);

        /* Create keyboard listener */
        const keyboardListener = new NonogramKeyboardListener(state);
        window.addEventListener("keyup", ev => keyboardListener.onKeyUp(ev));
        window.addEventListener("keydown", ev => keyboardListener.onKeyDown(ev));

        /* Add padding so that board is perfectly in the middle */
        const paddingLeft = Math.max(0, rightCol.clientWidth - leftCol.clientWidth);
        nonogramContainer.style.paddingLeft = paddingLeft + "px";

        const paddingRight = Math.max(0, leftCol.clientWidth - rightCol.clientWidth);
        nonogramContainer.style.paddingRight = paddingRight + "px";


        return this.view;
    }

    cleanup(): void {
        // Nothing to do
    }
    
}